'use client';
import dynamic from 'next/dynamic';
import { responsiveBreakpointsPx } from '@/constants';
import { useMediaQuery } from '@/hooks';
import { type SocialMediaIconLinkItem } from 'de-sxm-reactlib/components/atoms/SocialMediaIconLinks';
import FullFooter from 'de-sxm-reactlib/components/molecules/NavFooter/Full/FullFooter';
import SimpleFooter from 'de-sxm-reactlib/components/molecules/NavFooter/Simple/SimpleFooter';
import type {
    FooterItem,
    SecondaryLink,
    BasicFooterLinks,
    AppLink,
} from 'de-sxm-reactlib/components/molecules/NavFooter';
const Grid = dynamic(() => import('de-sxm-reactlib/components/atoms/Layout/Grid/Grid'));
import {
    DownloadButtonProps,
    Variant,
} from 'de-sxm-reactlib-2/components/molecules/AppDownloadButtons/AppDownloadButtons';
const AppDownloadButtons = dynamic(
    () => import('de-sxm-reactlib-2/components/molecules/AppDownloadButtons/AppDownloadButtons'),
);

interface Props {
    type: 'Full' | 'Simple' | 'Basic';
    primaryLinks: FooterItem[];
    socialLinks: SocialMediaIconLinkItem[];
    secondaryLinks: SecondaryLink[];
    copyrightText: React.ReactNode;
    basicFooterLinks: BasicFooterLinks;
    playStore: AppLink;
    appleStore: AppLink;
}

export default function ClientNavFooter({
    type,
    primaryLinks,
    socialLinks,
    secondaryLinks,
    copyrightText,
    playStore,
    appleStore,
}: Props) {
    const isViewportDesktop = useMediaQuery(`(min-width: ${responsiveBreakpointsPx.md}px)`) && type !== 'Basic';

    const downloadButtons: DownloadButtonProps[] = [
        {
            badgeSrc: appleStore.badgeSrc,
            storeLink: appleStore.storeLink,
            type: Variant.appleStore,
        },
        {
            badgeSrc: playStore.badgeSrc,
            storeLink: playStore.storeLink,
            type: Variant.playStore,
        },
    ];

    function renderFooter() {
        if (type === 'Full') {
            return (
                <FullFooter
                    primaryLinks={primaryLinks}
                    socialMediaItems={socialLinks}
                    appDownloadButtons={<AppDownloadButtons items={downloadButtons} />}
                    secondaryLinks={secondaryLinks}
                    copyrightText={<>{copyrightText}</>}
                />
            );
        } else if (type === 'Simple') {
            return (
                <SimpleFooter
                    primaryLinks={primaryLinks}
                    secondaryLinks={secondaryLinks}
                    socialMediaItems={socialLinks}
                    appDownloadButtons={<AppDownloadButtons items={downloadButtons} />}
                    copyrightText={<>{copyrightText}</>}
                />
            );
        }
    }

    return (
        <Grid container noPadding={!isViewportDesktop}>
            <Grid col={{ sm: 12 }}>
                <footer data-componenttype={`${type}Footer`} data-swiftype-index="false">
                    {renderFooter()}
                </footer>
            </Grid>
        </Grid>
    );
}
